import { useEffect, useRef } from 'react';

// ── Shared CRM live-update bus ──────────────────────────────────────────────
// A single EventSource is shared by every component that uses useLiveRefresh, so
// the browser keeps just one connection open no matter how many CRM screens are
// mounted. When the server pushes "crm:changed", every subscriber re-fetches
// instantly — no manual refresh. Focus + interval remain as resilient fallbacks.
let _es = null;
let _refCount = 0;
const _subscribers = new Set();

function _openStream() {
  if (_es || typeof window === 'undefined' || typeof window.EventSource === 'undefined') return;
  let token = '';
  try { token = localStorage.getItem('flowup_token') || ''; } catch (e) { /* ignore */ }
  try {
    _es = new EventSource(`/api/crm/events?token=${encodeURIComponent(token)}`);
    _es.addEventListener('crm:changed', () => {
      _subscribers.forEach(fn => { try { fn(); } catch (e) { /* ignore */ } });
    });
    _es.onerror = () => { /* EventSource auto-reconnects; ignore transient errors */ };
  } catch (e) { _es = null; }
}

function _closeStreamIfIdle() {
  if (_refCount <= 0 && _es) { try { _es.close(); } catch (e) {} _es = null; }
}

// Re-runs the latest `fn` instantly on a live server event, plus on focus/visibility
// and a slow interval as fallback. Uses a ref so it always calls the freshest closure.
export default function useLiveRefresh(fn, intervalMs = 30000) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const run = () => { try { ref.current && ref.current(); } catch (e) { /* ignore */ } };

    // Subscribe to the shared live stream for instant updates.
    _subscribers.add(run);
    _refCount += 1;
    _openStream();

    const onFocus = () => run();
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const t = setInterval(run, intervalMs);

    return () => {
      _subscribers.delete(run);
      _refCount -= 1;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(t);
      _closeStreamIfIdle();
    };
  }, [intervalMs]);
}
